import {
  ChatEventFragment,
  ConversationsV2ToolConfirmationResponseDecision,
} from '@spotify/confidence-graphql';

import {
  ToolExtensionOptions,
  ToolGroupEntry,
} from '../../extensions/tool-extension';

export type DisplayItem =
  | { type: 'event'; event: ChatEventFragment; key: string }
  | { type: 'tool-group'; entries: ToolGroupEntry[]; key: string };

const HIDDEN_TOOLS = new Set(['file_read', 'mcp__confidence__report_feedback']);

export const getToolResultsByCallName = (
  events: ChatEventFragment[],
): Map<string, NonNullable<ToolGroupEntry['result']>> => {
  const results = new Map<string, NonNullable<ToolGroupEntry['result']>>();
  for (const event of events) {
    const result = event.toolResult;
    const call = result?.toolCallEvent;
    if (result && call?.__typename === 'ConversationsV2ChatEvent') {
      results.set(call.name, {
        content: result.content,
        success: result.success,
      });
    }
  }
  return results;
};

export const hasPendingToolConfirmationRequest = (
  events: ChatEventFragment[],
): boolean => {
  const resolvedRequestNames = new Set<string>();
  for (const event of events) {
    const requestEvent = event.toolConfirmationResponse?.toolRequestEvent;
    if (requestEvent?.__typename === 'ConversationsV2ChatEvent') {
      resolvedRequestNames.add(requestEvent.name);
    }
  }
  const lastUserMessageIndex = events.findLastIndex(event =>
    Boolean(event.userMessage),
  );
  const currentTurnEvents =
    lastUserMessageIndex === -1
      ? events
      : events.slice(lastUserMessageIndex + 1);
  return currentTurnEvents.some(
    event =>
      Boolean(event.toolConfirmationRequest) &&
      !resolvedRequestNames.has(event.name),
  );
};

export const groupChatEvents = (
  events: ChatEventFragment[],
  toolExtensions: ToolExtensionOptions[],
  {
    groupConsecutiveTools = true,
    groupAcrossThinking = true,
  }: { groupConsecutiveTools?: boolean; groupAcrossThinking?: boolean } = {},
): DisplayItem[] => {
  // Build maps for pairing ToolCalls with their ToolResults.
  // Entries are created at the ToolCall position to preserve call order.
  const toolCallMap = new Map<
    string,
    { tool: string; displayName?: string | null; input?: unknown }
  >();
  const toolResultByCallName = getToolResultsByCallName(events);
  for (const event of events) {
    if (event.toolCall) {
      toolCallMap.set(event.name, {
        tool: event.toolCall.tool,
        displayName: event.toolCall.displayName,
        input: event.toolCall.input,
      });
    }
  }

  // Build a map of ToolConfirmationRequest event name → decision so we can hide
  // requests that have already been approved (keeping the thread tidy after
  // the user allows a tool).
  const confirmationDecisionByRequestName = new Map<
    string,
    ConversationsV2ToolConfirmationResponseDecision
  >();
  for (const event of events) {
    const response = event.toolConfirmationResponse;
    if (
      response &&
      response.toolRequestEvent.__typename === 'ConversationsV2ChatEvent' &&
      !confirmationDecisionByRequestName.has(response.toolRequestEvent.name)
    ) {
      confirmationDecisionByRequestName.set(
        response.toolRequestEvent.name,
        response.decision,
      );
    }
  }

  const findExtension = (tool: string) =>
    toolExtensions.find(e => e.predicate(tool));

  const items: DisplayItem[] = [];
  let pendingGroup: { key: string; entries: ToolGroupEntry[] } | undefined;
  let pendingThinking: DisplayItem | undefined;

  const flushGroup = () => {
    if (pendingGroup) {
      items.push({
        type: 'tool-group',
        entries: pendingGroup.entries,
        key: pendingGroup.key,
      });
      pendingGroup = undefined;
    }
    if (pendingThinking) {
      items.push(pendingThinking);
      pendingThinking = undefined;
    }
  };

  const addToGroup = (entry: ToolGroupEntry, eventName: string) => {
    if (!groupConsecutiveTools) {
      items.push({ type: 'tool-group', entries: [entry], key: eventName });
      return;
    }
    if (!pendingGroup) {
      pendingGroup = { key: eventName, entries: [] };
    }
    pendingGroup.entries.push(entry);
  };

  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const payload = {
      userMessage: event.userMessage,
      assistantMessage: event.assistantMessage,
      thinking: event.thinking,
      toolCall: event.toolCall,
      toolResult: event.toolResult,
      toolConfirmationRequest: event.toolConfirmationRequest,
      toolConfirmationResponse: event.toolConfirmationResponse,
      userInterrupt: event.userInterrupt,
      error: event.error,
    };

    if (payload.toolCall) {
      if (HIDDEN_TOOLS.has(payload.toolCall.tool)) {
        continue;
      }

      pendingThinking = undefined;
      const result = toolResultByCallName.get(event.name);
      const entry: ToolGroupEntry = {
        tool: payload.toolCall.tool,
        eventName: event.name,
        displayName: payload.toolCall.displayName || undefined,
        input: payload.toolCall.input,
        result,
      };
      const ext = findExtension(payload.toolCall.tool);
      if (ext?.display === 'prominent') {
        flushGroup();
        items.push({
          type: 'tool-group',
          entries: [entry],
          key: event.name,
        });
      } else {
        addToGroup(entry, event.name);
      }
      continue;
    }

    if (payload.toolResult) {
      const toolCallEventName =
        payload.toolResult.toolCallEvent?.__typename ===
        'ConversationsV2ChatEvent'
          ? payload.toolResult.toolCallEvent.name
          : undefined;
      // Skip results that were already paired at the ToolCall position.
      if (toolCallEventName && toolCallMap.has(toolCallEventName)) {
        continue;
      }
      // Orphaned result (no matching call).
      const tool = 'unknown';
      if (HIDDEN_TOOLS.has(tool)) {
        continue;
      }
      const entry: ToolGroupEntry = {
        tool,
        eventName: event.name,
        result: {
          content: payload.toolResult.content,
          success: payload.toolResult.success,
        },
      };
      pendingThinking = undefined;
      addToGroup(entry, event.name);
      continue;
    }

    if (payload.thinking) {
      if (pendingGroup) {
        if (!groupAcrossThinking) {
          flushGroup();
          items.push({ type: 'event', event, key: event.name });
          continue;
        }
        pendingThinking = { type: 'event', event, key: event.name };
        continue;
      }
      flushGroup();
      items.push({ type: 'event', event, key: event.name });
      continue;
    }

    if (payload.toolConfirmationResponse) {
      if (
        payload.toolConfirmationResponse.decision ===
        ConversationsV2ToolConfirmationResponseDecision.AllowAll
      ) {
        continue;
      }
      items.push({ type: 'event', event, key: event.name });
      continue;
    }

    if (payload.toolConfirmationRequest) {
      const decision = confirmationDecisionByRequestName.get(event.name);
      if (decision) {
        continue;
      }
    }

    flushGroup();
    items.push({ type: 'event', event, key: event.name });
  }

  flushGroup();
  return items;
};
